import type { ReputationScoreDto } from '@up2stay/up2promo-api-client';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@up2stay/up2promo-ui';
import { Info } from 'lucide-react';

export type ScoreFormulaLabels = {
  title: string;
  intro: string;
  weight: string;
  components: {
    rating: string;
    volume: string;
    responseRate: string;
    sentiment: string;
    responseSpeed: string;
  };
};

const COMPONENT_LABEL_KEYS: Record<
  string,
  keyof ScoreFormulaLabels['components']
> = {
  rating: 'rating',
  volume: 'volume',
  responseRate: 'responseRate',
  sentiment: 'sentiment',
  responseSpeed: 'responseSpeed',
};

export function ReputationScoreFormulaPopover({
  score,
  labels,
}: {
  score: ReputationScoreDto;
  labels: ScoreFormulaLabels;
}) {
  const intro = labels.intro.replace('{version}', score.scoreVersion);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 sm:size-8"
          aria-label={labels.title}
        >
          <Info className="size-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3 text-sm">
        <div>
          <p className="font-medium">{labels.title}</p>
          <p className="text-muted-foreground mt-1 text-xs">{intro}</p>
        </div>
        <ul className="space-y-2">
          {score.scoreComponents
            .filter((c) => c.weight > 0)
            .map((component) => {
              const labelKey = COMPONENT_LABEL_KEYS[component.key];
              const name = labelKey
                ? labels.components[labelKey]
                : component.key;
              return (
                <li
                  key={component.key}
                  className="flex items-start justify-between gap-3"
                >
                  <span className="text-muted-foreground min-w-0 flex-1">
                    {name}{' '}
                    <span className="tabular-nums">
                      ({Math.round(component.weight * 100)}%)
                    </span>
                  </span>
                  <span className="shrink-0 font-medium tabular-nums">
                    {Math.round(component.value)}
                  </span>
                </li>
              );
            })}
        </ul>
        <p className="text-muted-foreground border-t pt-2 text-xs">
          {labels.weight.replace('{score}', String(score.score))}
        </p>
      </PopoverContent>
    </Popover>
  );
}

export function formatReputationScoreValue(score: ReputationScoreDto): string {
  return score.scoreConfidence === 'low'
    ? `~${score.score}`
    : String(score.score);
}

export function reputationScoreFooter(
  score: ReputationScoreDto,
  labels: {
    lowConfidence: string;
    lowConfidenceDesc: string;
    scoreStrong: string;
    scoreFair: string;
    scoreWeak: string;
    scoreDesc: string;
  },
): {
  trend: string;
  direction: 'up' | 'down' | 'neutral';
  description: string;
} {
  if (score.scoreConfidence === 'low') {
    return {
      trend: labels.lowConfidence,
      direction: 'neutral',
      description: labels.lowConfidenceDesc,
    };
  }
  if (score.score >= 75) {
    return {
      trend: labels.scoreStrong,
      direction: 'up',
      description: labels.scoreDesc,
    };
  }
  if (score.score >= 50) {
    return {
      trend: labels.scoreFair,
      direction: 'neutral',
      description: labels.scoreDesc,
    };
  }
  return {
    trend: labels.scoreWeak,
    direction: 'down',
    description: labels.scoreDesc,
  };
}
